import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import { photosService } from '@/api/services/photos.service'
import { writeStoredPhotosTrayAlbum } from '@/lib/photos/photos-tray-album-storage'
import {
  getRecommendForSpreadAlbumValue,
  readPersistedPhotosTrayAlbumValue,
  resolvePhotosTrayAlbumForOpenSpread,
} from '@/lib/photos/resolve-photos-tray-album'

const RECOMMEND_FOR_SPREAD_ALBUM_VALUE = getRecommendForSpreadAlbumValue()

/**
 * The Photos tray's selected album: starts from the persisted album, and on opening a spread
 * switches (for this session only) to whatever the spread's recommendations resolve to — unless
 * the user already picked an album for that spread.
 */
export function usePhotosTrayAlbum(spreadId: string | null) {
  const spreadAlbumContextAppliedRef = useRef<string | null>(null)
  const userSelectedAlbumRef = useRef(false)
  const [albumValue, setAlbumValue] = useState<string | null>(() =>
    readPersistedPhotosTrayAlbumValue()
  )

  const handleAlbumChange = useCallback((nextValue: string | null) => {
    if (!nextValue) return

    userSelectedAlbumRef.current = true
    setAlbumValue(nextValue)
    writeStoredPhotosTrayAlbum(nextValue)
  }, [])

  const { data: spreadRecommendPhotos, isFetched: isSpreadRecommendFetched } = useQuery({
    queryKey: queryKeys.photos.albumPhotos(RECOMMEND_FOR_SPREAD_ALBUM_VALUE, spreadId),
    queryFn: () => photosService.getRecommendedForSpreadPhotos(spreadId!),
    enabled: Boolean(spreadId),
    staleTime: 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    userSelectedAlbumRef.current = false
    spreadAlbumContextAppliedRef.current = null
  }, [spreadId])

  useEffect(() => {
    if (!spreadId || !isSpreadRecommendFetched) return
    if (spreadAlbumContextAppliedRef.current === spreadId) return
    if (userSelectedAlbumRef.current) {
      spreadAlbumContextAppliedRef.current = spreadId
      return
    }

    spreadAlbumContextAppliedRef.current = spreadId
    // Session override only — keep saved album in localStorage for when NS has no recommendations.
    setAlbumValue(resolvePhotosTrayAlbumForOpenSpread(spreadRecommendPhotos ?? []))
  }, [isSpreadRecommendFetched, spreadId, spreadRecommendPhotos])

  return { albumValue, handleAlbumChange }
}
